import { CallInformation } from '../../features/calls/call-information.model';
import { toLocalIsoDay } from './iso-date.util';

/** Absolute route path of the Call Viewer (`CallsComponent`). */
export const CALL_VIEWER_PATH = '/calls';

export type CallViewerSource = 'customer' | 'ua' | 'ps';

export interface CallViewerLinkIds {
  customerId: number | string;
  userAccountId?: number | string;
  phoneServiceId?: number | string;
}

export interface CallViewerLink {
  /** Router commands for absolute navigation to the Call Viewer. */
  commands: string[];
  /** Query params consumed by `CallsComponent.hydrateFromQueryParams()`. */
  queryParams: Record<string, string>;
}

/**
 * Single source of truth for "Open Call Viewer" jumps from UA / PS / Customer pages.
 *
 * The Call Viewer (`/calls`, `CallsComponent.hydrateFromQueryParams`) reads `customerId`,
 * `userAccountId`, `phoneServiceId` from URL query params on init and hydrates its filter form.
 * PS jumps pass `phoneServiceId` directly — the Call Viewer maps it onto the shared
 * userAccountId form control via a seeded selectedUserAccountHit with kind: 'ps'.
 */
export function buildCallViewerLink(
  source: CallViewerSource,
  ids: CallViewerLinkIds,
): CallViewerLink {
  const queryParams: Record<string, string> = { customerId: String(ids.customerId) };
  if (source === 'ua') {
    if (ids.userAccountId === undefined) {
      throw new Error('buildCallViewerLink: source="ua" requires userAccountId');
    }
    queryParams['userAccountId'] = String(ids.userAccountId);
  } else if (source === 'ps') {
    if (ids.phoneServiceId === undefined) {
      throw new Error('buildCallViewerLink: source="ps" requires phoneServiceId');
    }
    queryParams['phoneServiceId'] = String(ids.phoneServiceId);
  }
  return { commands: [CALL_VIEWER_PATH], queryParams };
}

/**
 * Build the Call Viewer link for jumping straight to a specific call found via the
 * cchsid lookup (global search / deep link). `customerId` and the derived `singleDate`
 * (the call's local day, per `toLocalIsoDay`) are only added when the call carries a
 * `customerId` AND `timestamp` parses to a valid date — a call without a customer (A6),
 * or with an unparseable timestamp (defensive: the contract says non-null), opens the
 * dialog with no list filters instead of running a dateless list search for the customer.
 * @ticket EB-4153
 */
export function buildCallDetailsLink(call: CallInformation): CallViewerLink {
  const queryParams: Record<string, string> = { cchsid: call.cchsid };
  const date = new Date(call.timestamp);
  if (call.customerId && !Number.isNaN(date.getTime())) {
    queryParams['customerId'] = call.customerId;
    queryParams['singleDate'] = toLocalIsoDay(date);
  }
  return { commands: [CALL_VIEWER_PATH], queryParams };
}
